import { capitalize } from '@enmon/commons';
import { Granularity } from '@enmon/commons/lib/enmon';
import dayjs, { type Dayjs } from 'dayjs';
import type { IntlShape } from 'react-intl';

const FORMAT_FULLDATE = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * this function converts date stored in UTC in the DB to the same date including users TZ
 * e.g. 2020-12-31T23:59:59 would be 2020-01-01 in +1 TZ but we still want to display 2020-12-31 to the user
 */
export function fromUtcToLocalTz(dateString: string | number | Date | Dayjs | undefined) {
  const timezoneOffset = new Date(
    dayjs.isDayjs(dateString) ? dateString.toDate() : (dateString ?? Date.now()),
  ).getTimezoneOffset();
  return dayjs(dateString).add(timezoneOffset, 'minute').toDate();
}

/**
 * opposite function that converts date entered with TZ to UTC format string stored in the DB
 * e.g. 2020-12-31T21:59:59+2 but we want to store 2020-12-31T23:59:59 in the DB
 */
export function fromLocalTzToUtc(date: dayjs.ConfigType): string {
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
}

export function formatDate({
  date,
  intl,
  granularity,
  short,
  timeZone,
}: {
  date: string | number | Date;
  intl: IntlShape;
  granularity?: Granularity;
  /**
   * NOTE: short basically means only time information is displayed for Hour and Quarterhour granularity which is wrong, formatTime should be used in that case
   */
  short?: boolean;
  timeZone?: string;
}): string {
  if (!date) return '';

  let format = {};

  switch (granularity) {
    case Granularity.Year:
      format = { year: 'numeric' };
      break;
    case Granularity.Month:
      format = {
        year: 'numeric',
        month: 'numeric',
      };
      break;
    case Granularity.Week: {
      const dateObject = dayjs.utc(date);
      if (!dateObject.isValid()) return '';
      return `${dateObject.isoWeek()}/${dateObject.year()}`;
    }
    case Granularity.Day:
      format = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      };
      break;
    case Granularity.Hour:
    case Granularity.Quarterhour:
      format = short
        ? {
            hour: '2-digit',
            minute: '2-digit',
          }
        : FORMAT_FULLDATE;
      break;
    case Granularity.Exact:
      format = short
        ? {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }
        : FORMAT_FULLDATE;
      break;
    case Granularity.Period: {
      const [start, end] = typeof date === 'string' ? date.split('/') : [];
      if (start && end) {
        return `${intl.formatDate(start, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })} - ${intl.formatDate(end, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}`;
      }
      break;
    }
    default:
      format = {
        year: 'numeric',
        month: 'numeric',
      };
  }

  return intl.formatDate(date, { ...format, timeZone });
}

/**
 * Short, capitalized, localized month name, e.g. "Led"/"Úno" (cs) or "Jan"/"Feb" (en).
 * Formatted in UTC so a month bucket stored at UTC midnight never drifts into a neighbouring month.
 */
export const formatMonthShort = (date: string | number | Date, intl: IntlShape): string =>
  capitalize(intl.formatDate(date, { month: 'short', timeZone: 'UTC' }));

/**
 * Short month + 2-digit year, e.g. "Led 25" (cs) or "Jan 25" (en). Formatted in UTC (see above).
 */
export const formatMonthShortWithYear = (date: string | number | Date, intl: IntlShape): string =>
  capitalize(intl.formatDate(date, { month: 'short', year: '2-digit', timeZone: 'UTC' }));

/**
 * Long month + year, e.g. "Leden 2026".
 */
export const formatMonthLong = (date: string | number | Date, intl: IntlShape): string =>
  capitalize(intl.formatDate(date, { month: 'long', year: 'numeric', timeZone: 'UTC' }));
